import { useEffect, useState } from "react";

/**
 * CookieConsentUi
 *
 * A modern, transparent cookie consent banner fixed to the bottom of the screen.
 * - Respects existing consent in localStorage under key: "analytics-consent"
 * - Uses brand colors via Tailwind theme CSS variables
 * - Accessible with keyboard navigation and proper aria attributes
 */
interface CookieConsentUiProps {
    /** Called after user accepts cookies */
    onAccept: () => void;
    /** Called after user declines cookies */
    onDecline: () => void;
}

export default function CookieConsentUi({ onAccept, onDecline }: CookieConsentUiProps) {
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        // Show only if user hasn't made a valid choice yet
        try {
            const consent = typeof window !== 'undefined'
                ? window.localStorage.getItem('analytics-consent')
                : null;

            console.log('consent', consent);

            // Treat anything other than explicit 'true' or 'false' as no choice
            if (consent !== 'true' && consent !== 'false') {
                setIsVisible(true);
            }
        } catch {
            // If localStorage is unavailable (privacy modes), show banner
            setIsVisible(true);
        }
    }, []);

    const handleAccept = () => {
        // Persist consent and notify caller (typically initializes analytics)
        localStorage.setItem('analytics-consent', 'true');
        setIsVisible(false);
        onAccept?.();
    };

    const handleDecline = () => {
        // Persist opt-out and notify caller
        localStorage.setItem('analytics-consent', 'false');
        setIsVisible(false);
        onDecline?.();
    };

    if (!isVisible) return null;

    return (
        <div
            role="dialog"
            aria-live="polite"
            aria-label="Cookie consent"
            className="fixed inset-x-0 bottom-0 z-50 px-3 sm:px-4 pb-3 sm:pb-4"
        >
            <div
                className="mx-auto max-w-7xl rounded-xl border border-border/60 bg-white/70 backdrop-blur supports-[backdrop-filter]:bg-white/55 shadow-lg sm:shadow-xl"
            >
                <div className="p-3 sm:p-4">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                        <div className="text-xs sm:text-sm text-foreground/80">
                            <p>
                                We use cookies and similar technologies to personalize content, measure performance,
                                and improve your experience.
                            </p>
                            <p className="mt-1">
                                By clicking <span className="font-semibold">Accept</span>, you consent to analytics
                                cookies as described in our privacy policy.
                            </p>
                        </div>
                        <div className="flex w-full sm:w-auto items-center gap-2 sm:gap-3">
                            <button
                                type="button"
                                onClick={handleDecline}
                                className="w-full sm:w-auto px-4 py-2 text-xs sm:text-sm rounded-md border border-foreground/30 text-foreground hover:bg-foreground/5 transition-colors"
                            >
                                Decline
                            </button>
                            <button
                                type="button"
                                onClick={handleAccept}
                                className="w-full sm:w-auto px-4 py-2 text-xs sm:text-sm rounded-md bg-[#16783a] text-primary-foreground border border-[#16783a] hover:opacity-90 transition-opacity"
                            >
                                Accept
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}